import React, { useEffect, useRef, useSyncExternalStore } from "react";

// Pointer-based drag & drop: open hand on hover, closed hand while held, highlighted drop target.
let state = { payload: null, over: null };
const subs = new Set();
const targets = new Map();
const emit = (patch) => { state = { ...state, ...patch }; subs.forEach((f) => f()); };
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
let justDragged = false;

export const setGrabbing = (on) => document.body.classList.toggle("mc-grabbing", !!on);
export const useDndState = () => useSyncExternalStore(subscribe, () => state);

function startDrag(e, payload, onStart, onEnd) {
  if (e.button !== 0) return;
  const blocker = e.target.closest("button, a, input, textarea, select, [data-no-drag]");
  if (blocker && blocker !== e.currentTarget) return;
  const src = e.currentTarget;
  const r = src.getBoundingClientRect();
  const sx = e.clientX, sy = e.clientY;
  let ghost = null;
  const move = (ev) => {
    if (!ghost) {
      if (Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 5) return;
      ghost = src.cloneNode(true);
      ghost.removeAttribute("data-testid");
      ghost.setAttribute("data-testid", "dnd-ghost");
      Object.assign(ghost.style, { position: "fixed", left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, margin: "0",
        zIndex: "9999", pointerEvents: "none", opacity: "0.95", rotate: "1.5deg", boxShadow: "0 18px 40px -12px rgba(114,63,255,.45)", transition: "none" });
      document.body.appendChild(ghost);
      setGrabbing(true);
      emit({ payload, over: null });
      onStart && onStart(payload);
    }
    ghost.style.translate = `${ev.clientX - sx}px ${ev.clientY - sy}px`;
    const el = document.elementFromPoint(ev.clientX, ev.clientY)?.closest("[data-dnd-target]");
    const id = el?.getAttribute("data-dnd-target");
    const t = id && targets.get(id);
    const over = t && t.accept(payload) ? id : null;
    if (over !== state.over) emit({ over });
  };
  const finish = (drop) => {
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("keydown", esc);
    if (!ghost) return;
    ghost.remove(); setGrabbing(false);
    const over = state.over;
    emit({ payload: null, over: null });
    justDragged = true; setTimeout(() => { justDragged = false; }, 0);
    if (drop && over) targets.get(over)?.onDrop(payload);
    onEnd && onEnd(payload);
  };
  const up = () => finish(true);
  const esc = (ev) => { if (ev.key === "Escape") finish(false); };
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("keydown", esc);
}

export function dragSource(payload, { enabled = true, onStart, onEnd } = {}) {
  if (!enabled) return {};
  return {
    "data-dnd-source": "",
    onPointerDown: (e) => startDrag(e, payload, onStart, onEnd),
    onClickCapture: (e) => { if (justDragged) { e.preventDefault(); e.stopPropagation(); } },
  };
}

// Wraps a drop area. `className` may be a function of { isOver, active } for highlight styling.
export function DropZone({ id, accept, onDrop, as: Tag = "div", className, children, ...rest }) {
  const ref = useRef({ accept, onDrop });
  ref.current = { accept, onDrop };
  useEffect(() => {
    targets.set(id, { accept: (p) => (ref.current.accept ? ref.current.accept(p) : true), onDrop: (p) => ref.current.onDrop(p) });
    return () => targets.delete(id);
  }, [id]);
  const { payload, over } = useDndState();
  const active = !!payload && (!accept || accept(payload));
  const isOver = active && over === id;
  return (
    <Tag data-dnd-target={id} data-dnd-over={isOver ? "true" : undefined} className={typeof className === "function" ? className({ isOver, active }) : className} {...rest}>
      {typeof children === "function" ? children({ isOver, active }) : children}
    </Tag>
  );
}
